/**
 * AMRITARA Phase 2 storefront interaction publisher.
 * Production release: 2026-08-10 (schema amritara_p2_v1).
 * Deploy once in the Shopify theme. This file publishes custom events only;
 * the Shopify custom pixel forwards the allow-listed events to GA4.
 */
(function () {
  'use strict';

  if (window.__amritaraP2TrackingInstalled) return;
  window.__amritaraP2TrackingInstalled = true;

  var SCHEMA_VERSION = 'amritara_p2_v1';

  var EVENT_NAMES = {
    CONTENT: 'product_content_interaction',
    REFILL_VIDEO: 'refill_video_click',
    RECOMMENDATION: 'product_recommendation_click',
    REVIEW: 'review_interaction',
    OPTION: 'product_option_select',
    QUANTITY: 'quantity_change',
    FAQ: 'faq_interaction',
    ROUTINE_START: 'routine_builder_start',
    ROUTINE_COMPLETE: 'routine_builder_complete'
  };

  function clean(value, maxLength) {
    return String(value == null ? '' : value)
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength || 100);
  }

  function slug(value) {
    return clean(value, 100)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  function safeUrl(value) {
    try {
      var url = new URL(value, location.origin);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
      return (url.origin + url.pathname).slice(0, 500);
    } catch (error) {
      return '';
    }
  }

  function productContext(element) {
    var form = element && element.closest
      ? element.closest('form[action*="/cart/add"], product-form')
      : null;
    if (!form && element && element.getAttribute) {
      var formId = element.getAttribute('form');
      form = formId ? document.getElementById(formId) : null;
    }
    if (!form && /^\/products\//.test(location.pathname)) {
      form = document.querySelector('form[action*="/cart/add"]');
    }
    var productIdInput = form && form.querySelector
      ? form.querySelector('input[name="product-id"]')
      : null;
    var pathMatch = location.pathname.match(/^\/products\/([^/?#]+)/);

    return {
      product_id: productIdInput ? clean(productIdInput.value, 50) : '',
      product_handle: pathMatch ? clean(pathMatch[1], 100) : '',
      page_location: (location.origin + location.pathname).slice(0, 500)
    };
  }

  function publish(name, data) {
    if (!window.Shopify || !Shopify.analytics ||
        typeof Shopify.analytics.publish !== 'function') return;

    Shopify.analytics.publish(name, Object.assign({
      schema_version: SCHEMA_VERSION
    }, data || {}));
  }

  function withContext(element, data) {
    return Object.assign(productContext(element), data || {});
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest
      ? event.target.closest('button, a, [role="tab"], summary')
      : null;
    if (!target) return;

    var label = clean(target.getAttribute('aria-label') || target.textContent, 120);
    var labelSlug = slug(label);
    var href = target.tagName === 'A' ? target.href : '';

    var isProductContentTab = target.getAttribute('role') === 'tab' &&
      /^(ingredients|details|directions)$/.test(labelSlug) &&
      target.getAttribute('aria-selected') !== 'true';
    var contentDetails = target.tagName === 'SUMMARY' ? target.closest('details') : null;
    var isProductContentAccordion = target.tagName === 'SUMMARY' &&
      /^(ingredients|details|directions)$/.test(labelSlug) &&
      contentDetails && !contentDetails.open;
    if (isProductContentTab || isProductContentAccordion) {
      publish(EVENT_NAMES.CONTENT, withContext(target, {
        interaction_type: isProductContentTab ? 'tab_open' : 'accordion_open',
        content_name: labelSlug
      }));
      return;
    }

    if (target.tagName === 'A' &&
        (/refill/i.test(label) || /cdn\.shopify\.com\/videos\//i.test(href))) {
      publish(EVENT_NAMES.REFILL_VIDEO, withContext(target, {
        link_text: clean(label, 100),
        link_url: safeUrl(href)
      }));
      return;
    }

    var recommendationSection = target.closest(
      'section.shopify-section--shop-the-look, section[id*="shop_the_look"], [data-section-type*="recommend"]'
    );
    if (!recommendationSection) {
      var ancestor = target.parentElement;
      while (ancestor && ancestor !== document.body) {
        if (ancestor.matches('section') &&
            /complete the ritual|recommend/i.test(ancestor.textContent || '')) {
          recommendationSection = ancestor;
          break;
        }
        ancestor = ancestor.parentElement;
      }
    }
    var isRecommendation = target.tagName === 'A' &&
      /\/products\//.test(target.pathname || '') &&
      recommendationSection &&
      /complete the ritual|recommend/i.test(recommendationSection.textContent || '');
    if (isRecommendation) {
      var recommendedMatch = target.pathname.match(/^\/products\/([^/?#]+)/);
      publish(EVENT_NAMES.RECOMMENDATION, withContext(target, {
        list_name: 'complete_the_ritual',
        source_product_handle: productContext(target).product_handle,
        recommended_product_handle: recommendedMatch ? clean(recommendedMatch[1], 100) : ''
      }));
      return;
    }

    if (target.matches('.quantity-selector__button')) {
      var quantityInput = target.parentElement &&
        target.parentElement.querySelector('input[type="number"], [name="quantity"]');
      var mainProductContainer = target.closest('product-rerender[id^="product-info-"]');
      var isQuickBuy = target.closest(
        'quick-buy-drawer, quick-buy-modal, [id*="quick-buy" i], [class*="quick-buy" i]'
      );
      var productForm = mainProductContainer &&
        mainProductContainer.querySelector('form[action*="/cart/add"] input[name="product-id"]');
      if (!quantityInput || !mainProductContainer || isQuickBuy || !productForm ||
          mainProductContainer.querySelector('input[name="on_success"][value="force_open_drawer"]')) return;
      var currentQuantity = quantityInput ? Number(quantityInput.value) : null;
      var direction = /increase/i.test(label) ? 'increase' :
        (/decrease/i.test(label) ? 'decrease' : 'unknown');
      var step = Number(quantityInput && quantityInput.step) || 1;
      var minimum = Number(quantityInput && quantityInput.min);
      var maximum = Number(quantityInput && quantityInput.max);
      var nextQuantity = currentQuantity +
        (direction === 'increase' ? step : direction === 'decrease' ? -step : 0);
      if (Number.isFinite(minimum)) nextQuantity = Math.max(minimum, nextQuantity);
      if (Number.isFinite(maximum) && maximum > 0) nextQuantity = Math.min(maximum, nextQuantity);
      publish(EVENT_NAMES.QUANTITY, withContext(target, {
        direction: direction,
        quantity: currentQuantity == null || Number.isNaN(currentQuantity)
          ? ''
          : nextQuantity
      }));
      return;
    }

    var reviewTarget = target.matches(
      '.yotpo-sr-bottom-line-button, #yotpo-main-widget-btn, .yotpo-read-more, [aria-label*="review" i], [aria-label^="Navigate to" i], [aria-label="Filter with media" i]'
    );
    if (reviewTarget) {
      var reviewType = 'review_control';
      if (target.id === 'yotpo-main-widget-btn') reviewType = 'write_review';
      else if (/jump to reviews/i.test(label)) reviewType = 'open_reviews';
      else if (target.matches('.yotpo-read-more')) reviewType = 'read_more';
      else if (/search reviews/i.test(label)) reviewType = 'search_reviews';
      else if (/navigate to/i.test(label)) reviewType = 'pagination';
      else if (/filter with media/i.test(label)) reviewType = 'media_filter';
      else if (/not helpful/i.test(label)) reviewType = 'not_helpful_vote';
      else if (/helpful/i.test(label)) reviewType = 'helpful_vote';
      else if (/rating/i.test(label)) reviewType = 'rating_filter';
      publish(EVENT_NAMES.REVIEW, withContext(target, {
        interaction_type: reviewType,
        interaction_label: reviewType
      }));
      return;
    }

    var faqContainer = target.closest && target.closest(
      '[class*="faq" i], [id*="faq" i], [data-section-type*="faq" i]'
    );
    if (target.tagName === 'SUMMARY' && faqContainer) {
      publish(EVENT_NAMES.FAQ, withContext(target, {
        interaction_type: target.parentElement && target.parentElement.open ? 'close' : 'open',
        question_label: slug(label).slice(0, 100)
      }));
    }
  }, true);

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.matches) return;

    if (input.matches('select[aria-label="Rating"], select[aria-label="Sort by"]')) {
      var reviewSelectType = input.getAttribute('aria-label') === 'Rating'
        ? 'rating_filter'
        : 'sort_reviews';
      publish(EVENT_NAMES.REVIEW, withContext(input, {
        interaction_type: reviewSelectType,
        interaction_label: reviewSelectType
      }));
      return;
    }

    if (!input.matches('input[type="radio"]')) return;
    var formId = input.getAttribute('form');
    var form = input.closest('form[action*="/cart/add"], product-form') ||
      (formId ? document.getElementById(formId) : null);
    if (!form) return;

    var fieldset = input.closest('fieldset');
    var optionLegend = fieldset && fieldset.querySelector('legend');
    var optionLabel = input.id
      ? document.querySelector('label[for="' + input.id + '"]')
      : null;

    publish(EVENT_NAMES.OPTION, withContext(input, {
      option_name: slug(optionLegend ? optionLegend.textContent : 'variant_option'),
      option_value: clean(
        optionLabel ? optionLabel.textContent : (input.getAttribute('aria-label') || input.value),
        100
      ),
      option_position: clean(input.getAttribute('data-option-position'), 10)
    }));
  }, true);
})();
